/**
 * Discount System React Query Hooks
 * Phase 3 Frontend - Query and mutation hooks for all discount operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import * as discountApi from '../api/discounts.api';
import { invoiceKeys } from './useInvoices';
import { discountKeys } from './discountKeys';
import {
  CreateCustomDiscountInput,
  UpdateCustomDiscountInput,
  UpdateVariableDiscountSettingsInput,
  GetCustomDiscountsParams,
  GetStatsParams,
  ApproveDiscountsInput,
  RemoveDiscountInput,
  ApplyOneOffDiscountInput,
} from '../types/discount.types';

// ============================================================================
// QUERY KEYS
// ============================================================================

// Defined in a leaf module to avoid an import cycle with useInvoices.
export { discountKeys } from './discountKeys';

// ============================================================================
// SETTING QUERIES
// ============================================================================

/**
 * Query hook: Get variable discount settings
 */
export function useVariableDiscountSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: discountKeys.settings(),
    queryFn: discountApi.getVariableDiscountSettings,
    enabled: options.enabled ?? true,
  });
}

/**
 * Mutation hook: Update variable discount settings
 */
export function useUpdateVariableDiscountSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: UpdateVariableDiscountSettingsInput) =>
      discountApi.updateVariableDiscountSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.settings() });
      enqueueSnackbar('Settings updated successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update settings';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ============================================================================
// CUSTOM DISCOUNT QUERIES
// ============================================================================

/**
 * Query hook: Get paginated list of custom discounts
 */
export function useCustomDiscounts(filters: GetCustomDiscountsParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: discountKeys.customsList(filters),
    queryFn: () => discountApi.getCustomDiscounts(filters),
    enabled: options.enabled ?? true,
  });
}

/**
 * Query hook: Get a specific custom discount
 */
export function useCustomDiscount(id: string) {
  return useQuery({
    queryKey: discountKeys.custom(id),
    queryFn: () => discountApi.getCustomDiscountById(id),
    enabled: !!id,
  });
}

/**
 * Mutation hook: Create custom discount
 */
export function useCreateCustomDiscount() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateCustomDiscountInput) =>
      discountApi.createCustomDiscount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.customs() });
      enqueueSnackbar('Discount created successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create discount';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Mutation hook: Update custom discount
 */
export function useUpdateCustomDiscount() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomDiscountInput }) =>
      discountApi.updateCustomDiscount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.customs() });
      enqueueSnackbar('Discount updated successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update discount';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Mutation hook: Delete custom discount
 */
export function useDeleteCustomDiscount() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => discountApi.deleteCustomDiscount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.customs() });
      enqueueSnackbar('Discount deleted successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete discount';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ============================================================================
// INVOICE DISCOUNT QUERIES
// ============================================================================

/**
 * Query hook: Get discount preview for invoice
 */
export function useDiscountPreview(invoiceId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: discountKeys.preview(invoiceId),
    queryFn: () => discountApi.getDiscountPreview(invoiceId),
    // ⚠️ The discounts router requires operations_pricing:read on EVERY route, so a
    // caller that may lack it (the Edit dialog) must gate this, or it 403s.
    enabled: !!invoiceId && (options.enabled ?? true),
  });
}

/**
 * Query hook: Get discount audit trail for invoice
 */
export function useDiscountAuditTrail(invoiceId: string) {
  return useQuery({
    queryKey: discountKeys.audit(invoiceId),
    queryFn: () => discountApi.getDiscountAuditTrail(invoiceId),
    enabled: !!invoiceId,
  });
}

/**
 * Query hook: Get invoice discount details
 */
export function useInvoiceDiscountDetails(invoiceId: string) {
  return useQuery({
    queryKey: discountKeys.invoiceDetails(invoiceId),
    queryFn: () => discountApi.getInvoiceDiscountDetails(invoiceId),
    enabled: !!invoiceId,
  });
}

/**
 * Mutation hook: Apply discounts to invoice
 */
export function useApplyDiscounts(invoiceId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: ApproveDiscountsInput) =>
      discountApi.approveDiscounts(invoiceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.preview(invoiceId) });
      queryClient.invalidateQueries({ queryKey: discountKeys.audit(invoiceId) });
      queryClient.invalidateQueries({ queryKey: discountKeys.invoiceDetails(invoiceId) });
      // Also invalidate invoice detail queries if they exist
      // The page's Total / Balance Due come from `useAdminInvoice`, keyed
      // `invoiceKeys.detail(id)` = ['invoices', 'detail', id]. A bare
      // ['invoices', id] prefix matches nothing, so the total stayed stale.
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      enqueueSnackbar('Discounts applied successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to apply discounts';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Mutation hook: apply a ONE-OFF, admin-typed discount to this invoice.
 *
 * Separate from `useApplyDiscounts` because that one posts `selectedDiscountIds`
 * (ids of pre-existing eligible discounts) and the server requires a non-empty array.
 */
export function useApplyOneOffDiscount(invoiceId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: ApplyOneOffDiscountInput) =>
      discountApi.applyOneOffDiscount(invoiceId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: discountKeys.preview(invoiceId) });
      queryClient.invalidateQueries({ queryKey: discountKeys.audit(invoiceId) });
      queryClient.invalidateQueries({ queryKey: discountKeys.invoiceDetails(invoiceId) });
      // The page's Total / Balance Due come from `useAdminInvoice`, keyed
      // `invoiceKeys.detail(id)` = ['invoices', 'detail', id]. A bare
      // ['invoices', id] prefix matches nothing, so the total stayed stale.
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      // The catalogue only changes when the save switch fired.
      if (variables.saveToCustomDiscounts) {
        queryClient.invalidateQueries({ queryKey: discountKeys.customs() });
      }
      enqueueSnackbar('Discount applied', { variant: 'success' });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to apply the discount';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Mutation hook: Remove discount from invoice
 */
export function useRemoveDiscount(invoiceId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ discountId, data }: { discountId: string; data: RemoveDiscountInput }) =>
      discountApi.removeDiscount(invoiceId, discountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.preview(invoiceId) });
      queryClient.invalidateQueries({ queryKey: discountKeys.audit(invoiceId) });
      queryClient.invalidateQueries({ queryKey: discountKeys.invoiceDetails(invoiceId) });
      // The page's Total / Balance Due come from `useAdminInvoice`, keyed
      // `invoiceKeys.detail(id)` = ['invoices', 'detail', id]. A bare
      // ['invoices', id] prefix matches nothing, so the total stayed stale.
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      enqueueSnackbar('Discount removed successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove discount';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Mutation hook: Recalculate discounts for invoice
 */
export function useRecalculateDiscounts(invoiceId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => discountApi.recalculateDiscounts(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.preview(invoiceId) });
      queryClient.invalidateQueries({ queryKey: discountKeys.audit(invoiceId) });
      queryClient.invalidateQueries({ queryKey: discountKeys.invoiceDetails(invoiceId) });
      // The page's Total / Balance Due come from `useAdminInvoice`, keyed
      // `invoiceKeys.detail(id)` = ['invoices', 'detail', id]. A bare
      // ['invoices', id] prefix matches nothing, so the total stayed stale.
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      enqueueSnackbar('Discounts recalculated successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to recalculate discounts';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ============================================================================
// REPORTING QUERIES
// ============================================================================

/**
 * Query hook: Get discount statistics
 */
export function useDiscountStats(params?: GetStatsParams) {
  return useQuery({
    queryKey: discountKeys.stats(params),
    queryFn: () => discountApi.getDiscountStats(params),
  });
}

/**
 * Query hook: Get pending discount approvals
 */
export function usePendingApprovals() {
  return useQuery({
    queryKey: discountKeys.pending(),
    queryFn: discountApi.getPendingApprovals,
  });
}

/**
 * Query hook: Get discount eligibility for families in a term
 */
export function useTermDiscountEligibility(termId: string | null) {
  return useQuery({
    queryKey: [...discountKeys.all, 'term-eligibility', termId],
    queryFn: () => discountApi.getTermDiscountEligibility(termId!),
    enabled: !!termId,
  });
}
